import React, { useState, useEffect } from 'react';
import {
    Drawer,
    IconButton,
    CircularProgress,
    Tooltip,
    Button,
    Typography,
    useMediaQuery,
    List,
    ListItem,
    ListItemText,
    Box
} from '@mui/material';
import { Menu as MenuIcon, Add as AddIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';

// const DATABASE_URL = import.meta.env.VITE_APP_CHATBOT;
const DATABASE_URL = "";
const Sidebar = ({ expanded, handleExpand, onSelectSession, currentSessionId, createNewSession }) => {
    const [chatHistory, setChatHistory] = useState([]);
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const [collapsed, setCollapsed] = useState(!expanded);
    const [loading, setLoading] = useState(true); // Loader state
    const [isFetched, setIsFetched] = useState(false);
    const [isnewSession, setNewsession] = useState(false);
    const loggedInUser = JSON.parse(localStorage.getItem('user'));

    useEffect(() => {
        if (expanded && loggedInUser) {
            fetchChatHistory();
        }
    }, [expanded, loggedInUser, isnewSession]);

    const fetchChatHistory = async () => {
        if (!loggedInUser || isFetched) return;
        setLoading(true);
        try {
            const response = await axios.get(`${DATABASE_URL}/sb_chat_bot/getChat?user_id=${loggedInUser.uid}`);

            setChatHistory(Array.isArray(response.data) ? response.data : []);
            setIsFetched(true);
        } catch (error) {
            console.error('Error fetching chat history:', error);
        }
        setLoading(false);
    };

    const handleNewChatSession = async () => {
        const newSession = await createNewSession();
        setNewsession(true);

        setChatHistory((prevChatHistory) => [newSession, ...prevChatHistory]);
        onSelectSession(newSession);

        fetchChatHistory();
    };

    const handletoggle = () => {
        setCollapsed(!collapsed);
        handleExpand(collapsed);
    };
    return (
        <Drawer
            anchor="left"
            open
            variant="permanent"
            PaperProps={{
                sx: {
                    width: collapsed ? 75 : isSmall ? 300 : isTablet ? 450 : 310,
                    backgroundColor: isTablet ? (collapsed ? 'transparent' : '#f0f4f9') : 'color: theme.palette.secondary.main',
                    boxShadow: collapsed ? 0 : isTablet ? '1px 0 5px rgba(88, 88, 88, 0.2)' : 0,
                    borderRadius: collapsed ? 0 : isTablet ? '0 25px 25px 0' : 0,
                    transition: 'width 0.3s',
                    border: 'none'
                }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: expanded ? 'start' : 'center',
                    padding: '16px'
                }}
            >
                <Tooltip title={collapsed ? 'Open Sidebar' : 'Close Sidebar'}>
                    <IconButton onClick={handletoggle} color="inherit">
                        <MenuIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="New Chat">
                    {!(collapsed && isTablet) && (
                        <IconButton
                            onClick={handleNewChatSession}
                            sx={{
                                display: 'flex',
                                marginTop: expanded ? '55px !important' : '60px !important',
                                alignItems: 'center',
                                justifyContent: expanded ? 'flex-start' : 'center',
                                borderRadius: expanded ? '30px' : '50%',
                                width: expanded ? '60%' : 40,
                                height: expanded ? 'auto' : 40,
                                padding: expanded ? '10px' : 0,

                                backgroundColor: '#e6eaf1',
                                color: 'black',
                                textTransform: 'none',
                                boxShadow: 'none',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                '&:hover': {
                                    backgroundColor: '#e6eaf1',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            <AddIcon sx={{ color: 'black', marginRight: expanded ? '8px' : 0 }} />
                            {expanded && <Typography>New Chat</Typography>}
                        </IconButton>
                    )}
                </Tooltip>
            </Box>

            {!collapsed && (
                <List
                    sx={{
                        flexGrow: 1,
                        whiteSpace: 'nowrap', // Prevent wrapping of text
                        overflow: 'auto', // Hide overflow if any
                        textOverflow: 'ellipsis', // Add ellipsis if text overflows
                        height: 'calc(100vh - 64px)',
                        padding: '0 16px'
                    }}
                >
                    {loading && !isFetched ? ( // Show loader while loading data
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                            <CircularProgress sx={{ color: '#8aaee0' }} size={30} />
                        </Box>
                    ) : chatHistory.length > 0 ? (
                        chatHistory.map((session) => {
                            const secondUserMessage = session.messages?.find((msg) => msg?.sender === 'ChatBot') || null;
                            const sessionName = secondUserMessage ? secondUserMessage.text : 'New Session';
                            const isActiveSession = session.sessionId === currentSessionId;

                            return (
                                <ListItem
                                    key={session.sessionId}
                                    button
                                    onClick={() => onSelectSession(session)}
                                    sx={{
                                        backgroundColor: isActiveSession ? '#d3e3fd' : 'transparent',
                                        borderRadius: '8px',
                                        mb: 1,
                                        transition: '0.3s'
                                    }}
                                >
                                    <ListItemText
                                        primary={sessionName}
                                        primaryTypographyProps={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontSize: '17px',
                                            color: isActiveSession ? '#0842a0' : '#FF0000'
                                        }}
                                    />
                                </ListItem>
                            );
                        })
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ padding: 2 }}>
                            {loggedInUser ? 'No chat history available.' : 'Login to see history.'}
                        </Typography>
                    )}
                </List>
            )}
        </Drawer>
    );
};

Sidebar.propTypes = {
    expanded: PropTypes.bool.isRequired,
    handleExpand: PropTypes.func.isRequired,
    onSelectSession: PropTypes.func.isRequired,
    currentSessionId: PropTypes.string.isRequired
};

export default Sidebar;
